/**
 * Iter269.10 — Password-policy mirror of `backend/auth.py::validate_password`.
 *
 * Single source of truth on the client so AcceptInvite, PasswordReset,
 * FreeScan signup, Settings change-password, and any future surface stay
 * aligned with the server. If you ever loosen / tighten policy on the
 * backend, update this file in the same PR.
 *
 * Each rule is shaped `{ id, label, ok(pw) → boolean }` so a renderer can
 * either show a checklist (PasswordPolicyMeter) or just call `isPasswordValid`.
 */

export const PASSWORD_RULES = [
  {
    id: "length",
    label: "9+ characters",
    ok: (pw) => typeof pw === "string" && pw.length >= 9,
  },
  {
    id: "upper",
    label: "Uppercase letter",
    ok: (pw) => /[A-Z]/.test(pw || ""),
  },
  {
    id: "lower",
    label: "Lowercase letter",
    ok: (pw) => /[a-z]/.test(pw || ""),
  },
  {
    id: "number",
    label: "Number",
    ok: (pw) => /\d/.test(pw || ""),
  },
  {
    id: "special",
    // Same character set the backend regex accepts (see `validate_password`).
    label: "Special character",
    ok: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw || ""),
  },
];

export function isPasswordValid(pw) {
  return PASSWORD_RULES.every((r) => r.ok(pw));
}

export function passwordIssues(pw) {
  return PASSWORD_RULES.filter((r) => !r.ok(pw)).map((r) => r.id);
}


/* ────────────────────── Iter269.11 — Strength + generator ────────────── */

/** Curated symbol pool. Excludes whitespace, smart quotes, and characters
 *  that survive copy-paste poorly (` ' " \). Every char is accepted by
 *  the backend special-character regex. */
const _SYMBOLS  = "!@#$%^&*()_+-=[]{};:,.<>/?";
const _UPPERS   = "ABCDEFGHJKLMNPQRSTUVWXYZ";   // O dropped (looks like 0)
const _LOWERS   = "abcdefghijkmnopqrstuvwxyz"; // l dropped (looks like 1/I)
const _DIGITS   = "23456789";                   // 0/1 dropped
const _ALL      = _UPPERS + _LOWERS + _DIGITS + _SYMBOLS;

function _randInt(maxExclusive) {
  // crypto.getRandomValues for real entropy. Falls back to Math.random
  // only in environments where crypto isn't bound — never expected in
  // any real browser shipped after 2017.
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const a = new Uint32Array(1);
    window.crypto.getRandomValues(a);
    return a[0] % maxExclusive;
  }
  return Math.floor(Math.random() * maxExclusive);
}

function _pick(pool) {
  return pool[_randInt(pool.length)];
}

/**
 * Generate a password that's GUARANTEED to satisfy every PASSWORD_RULES
 * predicate. Length defaults to 16 — comfortable for password managers,
 * tight enough to type by hand if a user prefers to.
 */
export function generateStrongPassword(length = 16) {
  const minLen = Math.max(9, length);
  // Seed with one of each required class so isPasswordValid() is true
  // no matter how the rest of the draw goes.
  const out = [
    _pick(_UPPERS),
    _pick(_LOWERS),
    _pick(_DIGITS),
    _pick(_SYMBOLS),
  ];
  while (out.length < minLen) out.push(_pick(_ALL));
  // Fisher–Yates shuffle so the required chars aren't in predictable slots.
  for (let i = out.length - 1; i > 0; i--) {
    const j = _randInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join("");
}

/**
 * Strength score 0-100 = length contribution × character-class diversity.
 *  • Length: caps at 24 → linearly 0…60 points.
 *  • Diversity: each present class (upper/lower/digit/special) adds 10
 *    points (max 40).
 * The score is intentionally NOT used to gate submit — that's the role
 * of `isPasswordValid`. The bar is purely a visual reinforcement signal.
 */
export function passwordStrength(pw) {
  const s = typeof pw === "string" ? pw : "";
  if (!s) return { score: 0, label: "empty",  tier: "empty"  };
  const lenPts = Math.min(60, Math.round((s.length / 24) * 60));
  let div = 0;
  if (/[A-Z]/.test(s))                                       div += 10;
  if (/[a-z]/.test(s))                                       div += 10;
  if (/\d/.test(s))                                          div += 10;
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(s))       div += 10;
  const score = Math.min(100, lenPts + div);
  let tier  = "weak";
  let label = "weak";
  if (score >= 80)      { tier = "strong";  label = "strong";  }
  else if (score >= 60) { tier = "medium";  label = "good";    }
  else if (score >= 40) { tier = "okay";    label = "okay";    }
  else                  { tier = "weak";    label = "weak";    }
  return { score, label, tier };
}


/* ──────── Iter269.13 — Have I Been Pwned k-anonymity breach check ────── */

/**
 * SHA-1 hex of `s` via Web Crypto. Returns uppercase to match the format
 * HIBP returns range responses in. Throws if Web Crypto isn't available
 * (no real browser since 2017 lacks it; tests stub the export instead).
 */
export async function sha1HexUpper(s) {
  if (!(typeof globalThis !== "undefined" && globalThis.crypto?.subtle)) {
    throw new Error("Web Crypto subtle.digest unavailable.");
  }
  const data = new TextEncoder().encode(s);
  const buf  = await globalThis.crypto.subtle.digest("SHA-1", data);
  let out = "";
  const arr = new Uint8Array(buf);
  for (let i = 0; i < arr.length; i++) {
    out += arr[i].toString(16).padStart(2, "0");
  }
  return out.toUpperCase();
}

// Session-scoped cache keyed by the 5-char SHA-1 prefix. HIBP returns
// ~600-900 suffixes per prefix; caching saves both the network round-trip
// AND the linear scan when the same password reappears mid-session.
//
// Iter269.14 — hydrated from `sessionStorage` on first import + persisted
// after every successful range fetch. Same browser session → zero
// network cost even after page reloads / route changes / signup→reset
// navigations. Cleared automatically when the user closes the tab.
// We cap the cache at HIBP_CACHE_MAX prefixes to stay well under any
// sessionStorage quota (each prefix is ≤ ~32KB; 50 prefixes ≈ 1.5MB).
const HIBP_CACHE_KEY = "qt:hibp:v1";
const HIBP_CACHE_MAX = 50;
const _hibpCache = new Map();
const HIBP_RANGE_URL = "https://api.pwnedpasswords.com/range/";

function _hibpHasSessionStorage() {
  try {
    return typeof window !== "undefined" && !!window.sessionStorage;
  } catch {
    return false;
  }
}

function _hibpLoadCache() {
  if (!_hibpHasSessionStorage()) return;
  try {
    const raw = window.sessionStorage.getItem(HIBP_CACHE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "string") _hibpCache.set(k, v);
    }
  } catch { /* corrupt JSON / quota / SSR — ignore */ }
}

function _hibpPersistCache() {
  if (!_hibpHasSessionStorage()) return;
  try {
    // Evict oldest entries (insertion order) if we exceed the cap.
    while (_hibpCache.size > HIBP_CACHE_MAX) {
      const first = _hibpCache.keys().next().value;
      if (first === undefined) break;
      _hibpCache.delete(first);
    }
    const obj = Object.fromEntries(_hibpCache);
    window.sessionStorage.setItem(HIBP_CACHE_KEY, JSON.stringify(obj));
  } catch { /* quota exceeded, browser denied, SSR — ignore */ }
}

// Hydrate on module load. Safe even in SSR — guarded by the helper.
_hibpLoadCache();

/**
 * Returns `{ pwned: boolean, count: number }` for the supplied password,
 * or `null` on any network / fetch error (caller must treat null as
 * "unknown, fail-open").
 *
 * Privacy property: only the first 5 characters of the SHA-1 hash are
 * sent over the wire — HIBP cannot recover the original password from
 * that prefix because thousands of distinct passwords share it.
 *
 * `signal` (AbortController) lets the caller cancel a stale check when
 * the user keeps typing.
 */
export async function checkPwnedPassword(pw, signal) {
  if (!pw || pw.length < 4) return { pwned: false, count: 0 };
  let hash;
  try {
    hash = await sha1HexUpper(pw);
  } catch {
    return null;
  }
  const prefix = hash.slice(0, 5);
  const suffix = hash.slice(5);

  let body = _hibpCache.get(prefix);
  if (body === undefined) {
    try {
      const ctl = new AbortController();
      // If the caller passed a signal, chain it through.
      if (signal) signal.addEventListener("abort", () => ctl.abort(), { once: true });
      const r = await fetch(HIBP_RANGE_URL + prefix, {
        method: "GET",
        signal: ctl.signal,
        // HIBP's CORS allows browser requests from any origin.
        // No credentials, no custom headers (Add-Padding deliberately
        // omitted to keep response small).
      });
      if (!r.ok) return null;
      body = await r.text();
      _hibpCache.set(prefix, body);
      _hibpPersistCache();
    } catch {
      return null; // network / abort
    }
  }

  // Each line: `SUFFIX:COUNT\r\n`. We search the suffix linearly; the
  // file is ≤ ~32KB so a simple split is fine.
  const lines = body.split(/\r?\n/);
  for (const line of lines) {
    const colon = line.indexOf(":");
    if (colon < 35) continue;
    if (line.slice(0, colon) === suffix) {
      const count = parseInt(line.slice(colon + 1), 10) || 0;
      return { pwned: true, count };
    }
  }
  return { pwned: false, count: 0 };
}


